import {Button} from '@orbit-ui/components/button';
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from '@orbit-ui/components/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@orbit-ui/components/dialog';
import {Input} from '@orbit-ui/components/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@orbit-ui/components/table';
import {CheckIcon, LoaderCircleIcon, PlusIcon, RefreshCwIcon, TrashIcon} from 'lucide-react';
import * as React from 'react';
import {
  useApiKeysQuery,
  useCreateApiKeyMutation,
  useDeleteApiKeyMutation,
} from '@/features/core/api/api-hooks';

/**
 * API Key type definition
 * Matches the response structure from the backend
 */
type ApiKey = {
  id: string; // Keep as string for compatibility with deleteApiKeyMutation
  name: string;
  prefix: string;
  created_at: string;
  expires_at?: string;
};

/**
 * Response type for the API keys endpoint
 */
type ApiKeysResponse = ApiKey[];

/**
 * Response type for the create API key endpoint
 */
type CreateApiKeyResponse = {
  key: string;
  prefix: string;
};

// Format date string
const formatDate = (dateString: string) => new Date(dateString).toLocaleString();

export function ApiKeysManager() {
  const [error, setError] = React.useState<Error | null>(null);
  const [newKeyName, setNewKeyName] = React.useState('');
  const [newKeyData, setNewKeyData] = React.useState<{key: string; prefix: string} | null>(null);
  const [copySuccess, setCopySuccess] = React.useState(false);
  const [keyToDelete, setKeyToDelete] = React.useState<ApiKey | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  // Use React Query hooks instead of direct API client calls
  // Type the result of useApiKeysQuery to avoid unsafe assignments
  type ApiKeysQueryResult = {
    data: unknown;
    isLoading: boolean;
    refetch: () => Promise<unknown>;
  };

  const {data, isLoading, refetch: refetchApiKeys} = useApiKeysQuery() as ApiKeysQueryResult;

  // Cast the data to the correct type with a default empty array
  const apiKeys = (data as ApiKeysResponse) ?? [];

  const createApiKeyMutation = useCreateApiKeyMutation();
  const deleteApiKeyMutation = useDeleteApiKeyMutation();

  const isCreating = createApiKeyMutation.isPending;
  const isDeleting = deleteApiKeyMutation.isPending;

  // Function to create a new API key
  const createApiKey = async () => {
    if (!newKeyName.trim()) {
      setError(new Error('API key name is required'));
      return;
    }

    try {
      setError(null);
      const result = (await createApiKeyMutation.mutateAsync({
        name: newKeyName,
      })) as CreateApiKeyResponse;

      // Store the new key data (this will only be shown once)
      setNewKeyData({
        key: result.key,
        prefix: result.prefix,
      });

      // Clear the input field
      setNewKeyName('');

      // Refresh the API keys list
      await refetchApiKeys();
    } catch (error_) {
      setError(error_ instanceof Error ? error_ : new Error('Failed to create API key'));
      console.error('Error creating API key:', error_);
    }
  };

  // Function to open the delete confirmation dialog
  const confirmDeleteApiKey = (key: ApiKey) => {
    setKeyToDelete(key);
    setIsDeleteDialogOpen(true);
  };

  // Function to delete an API key
  const deleteApiKey = async () => {
    if (!keyToDelete) return;

    try {
      setError(null);
      await deleteApiKeyMutation.mutateAsync(keyToDelete.id);

      // Close the dialog
      setIsDeleteDialogOpen(false);
      setKeyToDelete(null);

      // Refresh the API keys list
      await refetchApiKeys();
    } catch (error_) {
      setError(error_ instanceof Error ? error_ : new Error('Failed to delete API key'));
      console.error('Error deleting API key:', error_);
    }
  };

  return (
    <>
      <Card className="w-full">
        <CardHeader>
          <CardTitle>API Keys</CardTitle>
          <CardDescription>Manage API keys for your organization</CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-4 text-red-500">Error: {error.message}</div>
          )}

          {/* New key display (only shown after creation) */}
          {newKeyData && (
            <div className="mb-6 rounded-md border border-green-200 bg-green-50 p-4">
              <h3 className="mb-2 font-medium text-green-800">API Key Created Successfully</h3>
              <p className="mb-2 text-sm text-green-700">
                This key will only be displayed once. Please copy it now and store it securely.
              </p>
              <div className="mb-2 break-all rounded border border-green-300 bg-white p-3 font-mono text-sm">
                {newKeyData.key}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(newKeyData.key);
                  setCopySuccess(true);
                  setTimeout(() => setCopySuccess(false), 2000);
                }}
              >
                {copySuccess ? (
                  <>
                    <CheckIcon className="mr-2 size-4 text-green-500" />
                    Copied!
                  </>
                ) : (
                  'Copy to Clipboard'
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="ml-2"
                onClick={() => setNewKeyData(null)}
              >
                Dismiss
              </Button>
            </div>
          )}

          {/* Create new key form */}
          <div className="mb-6 flex gap-4">
            <Input
              placeholder="Enter API key name"
              value={newKeyName}
              onChange={(event) => setNewKeyName(event.target.value)}
              className="flex-1"
            />
            <Button onClick={createApiKey} disabled={isCreating || !newKeyName.trim()}>
              {isCreating ? (
                <>
                  <LoaderCircleIcon className="mr-2 size-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <PlusIcon className="mr-2 size-4" />
                  Create API Key
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => refetchApiKeys()}
              title="Refresh API keys list"
            >
              <RefreshCwIcon className="size-4" />
              <span className="ml-2">Refresh list</span>
            </Button>
          </div>

          {/* API keys table */}
          {isLoading ? (
            <div className="flex h-32 items-center justify-center">
              <LoaderCircleIcon className="mr-2 size-8 animate-spin" />
              <span>Loading API keys...</span>
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              No API keys found. Create one using the form above.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Prefix</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {apiKeys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell className="font-medium">{key.name}</TableCell>
                    <TableCell className="font-mono">{key.prefix}...</TableCell>
                    <TableCell>{formatDate(key.created_at)}</TableCell>
                    <TableCell>{key.expires_at ? formatDate(key.expires_at) : 'Never'}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => confirmDeleteApiKey(key)}
                        title="Delete API key"
                      >
                        <TrashIcon className="size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete confirmation dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the API key &quot;{keyToDelete?.name}&quot;? This
              action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={deleteApiKey} disabled={isDeleting}>
              {isDeleting ? (
                <>
                  <LoaderCircleIcon className="mr-2 size-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete API Key'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
